import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, Globe, Save, Loader2, AlertTriangle, Eye, Upload, FileText, Shield } from 'lucide-react';
import { api } from '../../services/api';
import { compressIfImage } from '../../utils/imageCompress';

const PERSONAL_DOCS = [
  { key: 'id_passport', label: 'Personalausweis / Pass', required: true },
  { key: 'selfie_with_license', label: 'Selfie mit Führerschein', required: true },
  { key: 'transport_license', label: 'Personenbeförderungsschein', required: false },
  { key: 'criminal_record', label: 'Strafregisterauszug', required: false },
];

export function DriverProfileTab({ driverProfile, user, onRefresh, t }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(null);

  const docs = driverProfile?.documents || {};
  const isApproved = driverProfile?.status === 'approved';

  const startEdit = () => {
    setEditing(true);
    setForm({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      region: driverProfile?.region || '',
    });
    setResult(null);
  };

  const handleSave = async () => {
    if (!window.confirm('Achtung: Wenn Sie Ihre persönlichen Daten ändern, wird Ihr Konto gesperrt bis 366Transfer Ihre Änderungen überprüft hat. Möchten Sie fortfahren?')) return;
    setSaving(true);
    setResult(null);
    try {
      await api.put('/auth/profile', form);
      setEditing(false);
      setResult({ type: 'warning', msg: 'Profil aktualisiert. Ihr Konto wird überprüft.' });
      onRefresh();
    } catch (e) {
      setResult({ type: 'error', msg: e.response?.data?.detail || 'Fehler beim Speichern' });
    } finally { setSaving(false); }
  };

  const handleDocUpload = (docKey) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(docKey);
      try {
        const dataUrl = await compressIfImage(file);
        await api.post(`/driver/upload-document/${docKey}`, { document: dataUrl });
        onRefresh();
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Upload fehlgeschlagen';
        alert(`Upload fehlgeschlagen: ${msg}`);
      } finally { setUploading(null); }
    };
    input.click();
  };

  return (
    <div className="space-y-6" data-testid="driver-profile-tab">
      {/* Warning Banner */}
      {isApproved && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="profile-warning">
          <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Wichtiger Hinweis</p>
            <p className="text-xs text-amber-700 mt-0.5">Wenn Sie Ihre persönlichen Daten oder Dokumente ändern, wird Ihr Konto <b>automatisch gesperrt</b> bis 366Transfer die Änderungen überprüft und Ihr Konto wieder freigibt. Während dieser Zeit können Sie keine Angebote abgeben.</p>
          </div>
        </div>
      )}

      {driverProfile?.status === 'pending' && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <Shield size={20} className="text-orange-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-orange-800">Konto wird überprüft</p>
            <p className="text-xs text-orange-700 mt-0.5">Ihr Profil wird gerade von 366Transfer überprüft. Sie können derzeit keine Angebote abgeben.</p>
          </div>
        </div>
      )}

      {result && (
        <div className={`p-3 rounded-lg text-sm ${result.type === 'error' ? 'bg-red-50 text-red-700' : result.type === 'warning' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
          {result.msg}
        </div>
      )}

      {/* Personal Info */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><User size={18} /> Persönliche Daten</h3>
          {!editing && (
            <button onClick={startEdit} className="text-xs text-orange-500 hover:text-orange-600 font-medium" data-testid="edit-profile-btn">Bearbeiten</button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            {[
              { key: 'name', label: 'Name', icon: User },
              { key: 'email', label: 'E-Mail', icon: Mail, type: 'email' },
              { key: 'phone', label: 'Telefon', icon: Phone, type: 'tel' },
              { key: 'region', label: 'Region', icon: MapPin },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs text-slate-500 mb-1 flex items-center gap-1"><f.icon size={12} /> {f.label}</label>
                <input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid={`profile-input-${f.key}`} />
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50" data-testid="save-profile-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
              </button>
              <button onClick={() => setEditing(false)} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm">Abbrechen</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoField icon={User} label="Name" value={user?.name} />
            <InfoField icon={Mail} label="E-Mail" value={user?.email} />
            <InfoField icon={Phone} label="Telefon" value={user?.phone} />
            <InfoField icon={MapPin} label="Region" value={driverProfile?.region} />
            <InfoField icon={Globe} label="Land" value={driverProfile?.country || '-'} />
            <InfoField icon={Shield} label="Status" value={driverProfile?.status === 'approved' ? 'Aktiv' : driverProfile?.status === 'pending' ? 'Überprüfung' : driverProfile?.status} />
          </div>
        )}
      </div>

      {/* Personal Documents */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4"><FileText size={18} /> Persönliche Dokumente</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PERSONAL_DOCS.map(doc => {
            const val = docs[doc.key];
            const hasDoc = val && val !== 'pending' && val !== '';
            const isImage = hasDoc && (val.startsWith('data:') || val.startsWith('http'));
            const isCurrentlyUploading = uploading === doc.key;
            return (
              <div key={doc.key} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`profile-doc-${doc.key}`}>
                <div className="px-3 py-2 bg-slate-50 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-600">{doc.label}</span>
                  <span className={`text-xs ${hasDoc ? 'text-emerald-600 font-medium' : doc.required ? 'text-red-400' : 'text-slate-400'}`}>
                    {hasDoc ? 'Vorhanden' : doc.required ? 'Pflicht' : 'Optional'}
                  </span>
                </div>
                {isImage ? (
                  <div className="h-28 relative">
                    <img src={val} alt={doc.label} className="w-full h-full object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 flex">
                      <button onClick={() => { const w = window.open('', '_blank'); w.document.write(`<html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${val}" style="max-width:100%;max-height:100vh;object-fit:contain"/></body></html>`); w.document.title = doc.label; }} className="flex-1 bg-black/50 text-white text-[10px] text-center py-1.5 hover:bg-black/70 flex items-center justify-center gap-1">
                        <Eye size={10} /> Anzeigen
                      </button>
                      <label className="flex-1 bg-black/50 text-white text-[10px] text-center py-1.5 hover:bg-black/70 cursor-pointer flex items-center justify-center gap-1">
                        <Upload size={10} /> Ersetzen
                        <input type="file" accept="image/*" className="hidden" onChange={() => handleDocUpload(doc.key)} />
                      </label>
                    </div>
                  </div>
                ) : (
                  <label className="h-28 flex flex-col items-center justify-center bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleDocUpload(doc.key)}>
                    {isCurrentlyUploading ? <Loader2 size={18} className="animate-spin text-orange-500" /> : <Upload size={18} className="text-slate-300 mb-1" />}
                    <span className="text-xs text-slate-400">Hochladen</span>
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function InfoField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon size={14} className="text-slate-400 shrink-0" />
      <div>
        <p className="text-[10px] text-slate-400 uppercase">{label}</p>
        <p className="text-sm text-slate-800 font-medium">{value || '-'}</p>
      </div>
    </div>
  );
}
